import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useExchange } from '../contexts/ExchangeContext'
import { wizardApi } from '../services/api/wizard'

const USE_DB = import.meta.env.VITE_USE_DB_WIZARD !== 'false'
const DEBOUNCE_MS = 500

export interface HubData {
  exchangerPhaseOne?: {
    names: string
    homeAddress: string
    taxIdNumber: string
  }
  buyer?: {
    names: string
  }
  closingCompanySale?: {
    name: string
    address: string
    phone: string
    closerName: string
    closerEmail: string
  }
  relinquished?: {
    addressOrLegalDescription: string
  }
  identificationForm?: {
    exchangerNames: string
    relinquishedPropertyAddress: string
    idRule: '3-property' | '200-percent' | '95-percent' | ''
    identifiedProperties: string[]
  }
  exchangerPhaseTwo?: {
    names: string
    homeAddress: string
    taxIdNumber: string
  }
  seller?: {
    names: string
  }
  closingCompanyPurchase?: {
    name: string
    address: string
    phone: string
    closerName: string
    closerEmail: string
  }
  replacement?: {
    addressOrLegalDescription: string
  }
  disbursementForm?: {
    exchangerNames: string
    amount: string
    wireRecipient: string
    wireRecipientAddress: string
    wireRecipientBankName: string
    wireRecipientBankABA: string
    wireRecipientBankAccount: string
  }
  documents?: Array<{
    name: string
    uploaded: boolean
    required: boolean
    category: string
    signed?: boolean
  }>
  lastSavedAt?: string
  [key: string]: any // Allow additional fields from SaleWizard
}

export const useHubData = (
  scope: 'initial' | 'exchange' | 'acquisition' | 'phase1' | 'all' = 'all',
  userId?: string | null
): {
  data: HubData | null
  isLoading: boolean
  hydrated: boolean
  loadData: () => void
  saveData: (newData: Partial<HubData>) => boolean
  getData: <T extends keyof HubData>(key: T) => HubData[T] | undefined
  flush?: () => Promise<void>
  storageKey: string
  isSynced: boolean
  syncError: Error | null
} => {
  const { user } = useAuth()
  const { currentExchange } = useExchange()

  const getExchangeId = useCallback(() => {
    return currentExchange?.id || userId || user?.id || 'anon'
  }, [currentExchange?.id, userId, user?.id])

  const getRealExchangeId = useCallback(() => {
    return currentExchange?.id ?? null
  }, [currentExchange?.id])

  const getStorageKey = useCallback(() => {
    return `sale_wizard_v1_${getExchangeId()}_${scope}`
  }, [getExchangeId, scope])

  const [data, setData] = useState<HubData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hydrated, setHydrated] = useState(false)
  const [isSynced, setIsSynced] = useState(false)
  const [syncError, setSyncError] = useState<Error | null>(null)

  const dataRef = useRef<HubData | null>(null)
  const hasHydratedRef = useRef(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingDataRef = useRef<HubData | null>(null)

  useEffect(() => {
    dataRef.current = data
  }, [data])

  useEffect(() => {
    if (hydrated) hasHydratedRef.current = true
  }, [hydrated])

  // Flush pending DB write
  const flushToDb = useCallback(async () => {
    if (!USE_DB || !pendingDataRef.current) return
    const exchangeId = getRealExchangeId()
    if (!exchangeId) return

    const dataToSave = pendingDataRef.current
    pendingDataRef.current = null

    try {
      await wizardApi.saveHubData(exchangeId, scope, dataToSave)
      setIsSynced(true)
      setSyncError(null)
      localStorage.removeItem(`pending_sync_${exchangeId}_${scope}`)
    } catch (err) {
      console.error('Failed to sync hub data to DB:', err)
      setSyncError(err instanceof Error ? err : new Error('Sync failed'))
      setIsSynced(false)
      localStorage.setItem(`pending_sync_${exchangeId}_${scope}`, 'true')
    }
  }, [getRealExchangeId, scope])

  const loadData = useCallback(() => {
    try {
      const key = getStorageKey()
      const raw = localStorage.getItem(key)
      if (raw) {
        const parsed = JSON.parse(raw)
        setData(parsed)
        dataRef.current = parsed
      } else {
        setData(null)
        dataRef.current = null
      }
    } catch (error) {
      console.error('Failed to load hub data from localStorage:', error)
      setData(null)
      dataRef.current = null
    } finally {
      setIsLoading(false)
    }

    if (USE_DB) {
      const exchangeId = getRealExchangeId()
      if (!exchangeId) {
        setHydrated(true)
        return
      }

      if (!hasHydratedRef.current) setHydrated(false)
      wizardApi.getHubData(exchangeId, scope)
        .then((dbData) => {
          if (!dbData) {
            setHydrated(true)
            return
          }

          const dbTime = dbData.lastSavedAt ? new Date(dbData.lastSavedAt).getTime() : 0
          const localTime = dataRef.current?.lastSavedAt
            ? new Date(dataRef.current.lastSavedAt).getTime()
            : 0

          if (dbTime >= localTime) {
            setData(dbData)
            dataRef.current = dbData
            const key = getStorageKey()
            localStorage.setItem(key, JSON.stringify(dbData))
          }
          setIsSynced(true)
          setHydrated(true)
        })
        .catch((err) => {
          console.warn('Failed to load hub data from API, using localStorage:', err)
          setHydrated(true)
        })
    } else {
      setHydrated(true)
    }
  }, [getStorageKey, getRealExchangeId, scope])

  // Save data: localStorage immediately, debounce API write
  const saveData = useCallback((newData: Partial<HubData>) => {
    try {
      const key = getStorageKey()
      const existing = data || {}
      const updated: HubData = {
        ...existing,
        ...newData,
        lastSavedAt: new Date().toISOString()
      }

      // 1. Write to localStorage immediately
      localStorage.setItem(key, JSON.stringify(updated))
      setData(updated)

      // 2. Debounce API write
      if (USE_DB) {
        pendingDataRef.current = updated

        if (debounceRef.current) {
          clearTimeout(debounceRef.current)
        }
        debounceRef.current = setTimeout(() => {
          flushToDb()
        }, DEBOUNCE_MS)
      }

      return true
    } catch (error) {
      console.error('Failed to save hub data:', error)
      return false
    }
  }, [data, getStorageKey, flushToDb])

  const getData = useCallback(<T extends keyof HubData>(key: T): HubData[T] | undefined => {
    return data?.[key]
  }, [data])

  // Initial load
  useEffect(() => {
    loadData()
  }, [loadData])

  // Listen for custom events
  useEffect(() => {
    const handleUpdate = () => { loadData() }
    window.addEventListener('wizardDataUpdated', handleUpdate)
    window.addEventListener('salesHubCompleted', handleUpdate)
    window.addEventListener('identificationHubCompleted', handleUpdate)
    return () => {
      window.removeEventListener('wizardDataUpdated', handleUpdate)
      window.removeEventListener('salesHubCompleted', handleUpdate)
      window.removeEventListener('identificationHubCompleted', handleUpdate)
    }
  }, [loadData])

  // Flush pending writes on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      // Synchronous flush — write whatever is pending
      if (pendingDataRef.current && USE_DB) {
        const exchangeId = getExchangeId()
        if (exchangeId !== 'anon') {
          wizardApi.saveHubData(exchangeId, scope, pendingDataRef.current).catch(() => {})
        }
      }
    }
  }, [getExchangeId, scope])

  return {
    data,
    isLoading,
    hydrated,
    loadData,
    saveData,
    getData,
    flush: flushToDb,
    storageKey: getStorageKey(),
    isSynced,
    syncError,
  }
}
